import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { createAgencyAccount, updateAgencyAccount } from '../../../services/agencyManagementService';

const AgencyFormModal = ({ agency, onClose, onSuccess, currentLanguage = 'fr' }) => {
  const isEditing = !!agency;

  const labels = {
    fr: {
      createTitle: 'Créer une nouvelle agence',
      editTitle: 'Modifier l\'agence',
      agencyName: 'Nom de l\'agence',
      agencyNamePlaceholder: 'Ex: Alraqia Travels',
      email: 'Email de connexion',
      emailPlaceholder: 'admin@agence.ma',
      password: 'Mot de passe',
      passwordPlaceholder: 'Minimum 8 caractères',
      confirmPassword: 'Confirmer le mot de passe',
      contactName: 'Nom du responsable',
      contactNamePlaceholder: 'Ex: Mohamed Alami',
      phone: 'Téléphone',
      phonePlaceholder: '+212 6XX XXX XXX',
      city: 'Ville',
      cityPlaceholder: 'Ex: Casablanca',
      subscription: 'Plan d\'abonnement',
      status: 'Statut',
      cancel: 'Annuler',
      save: 'Enregistrer',
      create: 'Créer le compte',
      creating: 'Création...',
      saving: 'Enregistrement...',
      passwordNote: 'Laissez vide pour ne pas modifier le mot de passe',
      errors: {
        agencyNameRequired: 'Le nom de l\'agence est requis',
        emailRequired: 'L\'email est requis',
        emailInvalid: 'Email invalide',
        passwordRequired: 'Le mot de passe est requis',
        passwordMin: 'Minimum 8 caractères',
        passwordMatch: 'Les mots de passe ne correspondent pas',
        contactNameRequired: 'Le nom du responsable est requis',
      }
    },
    ar: {
      createTitle: 'إنشاء وكالة جديدة',
      editTitle: 'تعديل الوكالة',
      agencyName: 'اسم الوكالة',
      agencyNamePlaceholder: 'مثال: الراقية للسفر',
      email: 'بريد تسجيل الدخول',
      emailPlaceholder: 'admin@agency.ma',
      password: 'كلمة المرور',
      passwordPlaceholder: '8 أحرف على الأقل',
      confirmPassword: 'تأكيد كلمة المرور',
      contactName: 'اسم المسؤول',
      contactNamePlaceholder: 'مثال: محمد العلمي',
      phone: 'الهاتف',
      phonePlaceholder: '+212 6XX XXX XXX',
      city: 'المدينة',
      cityPlaceholder: 'مثال: الدار البيضاء',
      subscription: 'خطة الاشتراك',
      status: 'الحالة',
      cancel: 'إلغاء',
      save: 'حفظ',
      create: 'إنشاء الحساب',
      creating: 'جارٍ الإنشاء...',
      saving: 'جارٍ الحفظ...',
      passwordNote: 'اتركه فارغاً لعدم تغيير كلمة المرور',
      errors: {
        agencyNameRequired: 'اسم الوكالة مطلوب',
        emailRequired: 'البريد الإلكتروني مطلوب',
        emailInvalid: 'بريد إلكتروني غير صالح',
        passwordRequired: 'كلمة المرور مطلوبة',
        passwordMin: '8 أحرف على الأقل',
        passwordMatch: 'كلمتا المرور غير متطابقتين',
        contactNameRequired: 'اسم المسؤول مطلوب',
      }
    }
  };

  const l = labels?.[currentLanguage] || labels?.fr;

  const [formData, setFormData] = useState({
    agencyName: agency?.agencyName || '',
    email: agency?.email || '',
    password: '',
    confirmPassword: '',
    contactName: agency?.contactName || '',
    phone: agency?.phone || '',
    city: agency?.city || '',
    subscription: agency?.subscription || 'standard',
    status: agency?.status || 'active',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData?.agencyName?.trim()) newErrors.agencyName = l?.errors?.agencyNameRequired;
    if (!formData?.email?.trim()) {
      newErrors.email = l?.errors?.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = l?.errors?.emailInvalid;
    }
    if (!isEditing) {
      if (!formData?.password) {
        newErrors.password = l?.errors?.passwordRequired;
      } else if (formData?.password?.length < 8) {
        newErrors.password = l?.errors?.passwordMin;
      }
    } else if (formData?.password && formData?.password?.length < 8) {
      newErrors.password = l?.errors?.passwordMin;
    }
    if (formData?.password && formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = l?.errors?.passwordMatch;
    }
    if (!formData?.contactName?.trim()) newErrors.contactName = l?.errors?.contactNameRequired;
    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setServerError('');
    try {
      if (isEditing) {
        await updateAgencyAccount(agency?.id, formData);
      } else {
        await createAgencyAccount(formData);
      }
      onSuccess();
    } catch (err) {
      console.error('Form submit error:', err);
      setServerError(err?.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border shadow-warm-md w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name={isEditing ? 'Building2' : 'Plus'} size={20} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {isEditing ? l?.editTitle : l?.createTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-colors"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {serverError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{serverError}</p>
            </div>
          )}

          {/* Agency Name + Contact Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {l?.agencyName} <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={formData?.agencyName}
                onChange={(e) => handleChange('agencyName', e?.target?.value)}
                placeholder={l?.agencyNamePlaceholder}
                className={errors?.agencyName ? 'border-destructive' : ''}
              />
              {errors?.agencyName && <p className="text-xs text-destructive mt-1">{errors?.agencyName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {l?.contactName} <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={formData?.contactName}
                onChange={(e) => handleChange('contactName', e?.target?.value)}
                placeholder={l?.contactNamePlaceholder}
                className={errors?.contactName ? 'border-destructive' : ''}
              />
              {errors?.contactName && <p className="text-xs text-destructive mt-1">{errors?.contactName}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {l?.email} <span className="text-destructive">*</span>
            </label>
            <Input
              type="email"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              placeholder={l?.emailPlaceholder}
              className={errors?.email ? 'border-destructive' : ''}
              disabled={isEditing}
            />
            {errors?.email && <p className="text-xs text-destructive mt-1">{errors?.email}</p>}
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {l?.password} {!isEditing && <span className="text-destructive">*</span>}
              </label>
              <Input
                type="password"
                value={formData?.password}
                onChange={(e) => handleChange('password', e?.target?.value)}
                placeholder={l?.passwordPlaceholder}
                className={errors?.password ? 'border-destructive' : ''}
              />
              {errors?.password && <p className="text-xs text-destructive mt-1">{errors?.password}</p>}
              {isEditing && !errors?.password && (
                <p className="text-xs text-muted-foreground mt-1">{l?.passwordNote}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {l?.confirmPassword}
              </label>
              <Input
                type="password"
                value={formData?.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
                placeholder={l?.passwordPlaceholder}
                className={errors?.confirmPassword ? 'border-destructive' : ''}
              />
              {errors?.confirmPassword && <p className="text-xs text-destructive mt-1">{errors?.confirmPassword}</p>}
            </div>
          </div>

          {/* Phone + City */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{l?.phone}</label>
              <Input
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                placeholder={l?.phonePlaceholder}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{l?.city}</label>
              <Input
                type="text"
                value={formData?.city}
                onChange={(e) => handleChange('city', e?.target?.value)}
                placeholder={l?.cityPlaceholder}
              />
            </div>
          </div>

          {/* Subscription + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{l?.subscription}</label>
              <select
                value={formData?.subscription}
                onChange={(e) => handleChange('subscription', e?.target?.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              >
                <option value="basic">Plan Basique</option>
                <option value="standard">Plan Standard</option>
                <option value="premium">Plan Premium</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{l?.status}</label>
              <select
                value={formData?.status}
                onChange={(e) => handleChange('status', e?.target?.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              >
                <option value="active">Actif</option>
                <option value="trial">Essai</option>
                <option value="suspended">Suspendu</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {l?.cancel}
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={loading}
            iconName={isEditing ? 'Save' : 'UserPlus'}
            iconPosition="left"
          >
            {loading
              ? (isEditing ? l?.saving : l?.creating)
              : (isEditing ? l?.save : l?.create)
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgencyFormModal;
